import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Booking } from '../types/booking';

dayjs.extend(isSameOrBefore);

/** Orari selezionabili: 08:00–22:00, step 30 minuti. */
export const SLOT_START_HOUR = 8;
export const SLOT_END_HOUR = 22;
export const SLOT_MINUTES = 30;

export function generateTimeOptions(): string[] {
  const slots: string[] = [];
  let current = dayjs().hour(SLOT_START_HOUR).minute(0).second(0);
  const end = dayjs().hour(SLOT_END_HOUR).minute(0).second(0);

  while (current.isSameOrBefore(end)) {
    slots.push(current.format('HH:mm'));
    current = current.add(SLOT_MINUTES, 'minute');
  }

  return slots;
}

export function bookingStart(booking: Pick<Booking, 'startDate' | 'startTime'>): Dayjs {
  return dayjs(`${booking.startDate}T${booking.startTime}`);
}

export function bookingEnd(booking: Pick<Booking, 'endDate' | 'endTime'>): Dayjs {
  return dayjs(`${booking.endDate}T${booking.endTime}`);
}

/** Due intervalli [start, end) si sovrappongono. */
export function rangesOverlap(
  a: Pick<Booking, 'startDate' | 'endDate' | 'startTime' | 'endTime'>,
  b: Pick<Booking, 'startDate' | 'endDate' | 'startTime' | 'endTime'>
): boolean {
  const aStart = bookingStart(a);
  const aEnd = bookingEnd(a);
  const bStart = bookingStart(b);
  const bEnd = bookingEnd(b);
  return aStart.isBefore(bEnd) && bStart.isBefore(aEnd);
}

export function formatBookingRange(
  booking: Pick<Booking, 'startDate' | 'endDate' | 'startTime' | 'endTime'>
): string {
  const sameDay = booking.startDate === booking.endDate;
  if (sameDay) {
    return `${dayjs(booking.startDate).format('DD/MM/YYYY')} · ${booking.startTime} – ${booking.endTime}`;
  }
  return `${dayjs(booking.startDate).format('DD/MM/YYYY')} ${booking.startTime} → ${dayjs(booking.endDate).format('DD/MM/YYYY')} ${booking.endTime}`;
}

/** Intervallo di intersezione [start, end) tra due prenotazioni sovrapposte. */
export function formatOverlapPeriod(
  a: Pick<Booking, 'startDate' | 'endDate' | 'startTime' | 'endTime'>,
  b: Pick<Booking, 'startDate' | 'endDate' | 'startTime' | 'endTime'>
): string {
  const aStart = bookingStart(a);
  const aEnd = bookingEnd(a);
  const bStart = bookingStart(b);
  const bEnd = bookingEnd(b);
  const start = aStart.isAfter(bStart) ? aStart : bStart;
  const end = aEnd.isBefore(bEnd) ? aEnd : bEnd;
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
    return formatBookingRange(a);
  }
  if (start.isSame(end, 'day')) {
    return `${start.format('DD/MM/YYYY')} · ${start.format('HH:mm')} – ${end.format('HH:mm')}`;
  }
  return `${start.format('DD/MM/YYYY HH:mm')} → ${end.format('DD/MM/YYYY HH:mm')}`;
}

export function isValidRange(
  booking: Pick<Booking, 'startDate' | 'endDate' | 'startTime' | 'endTime'>
): boolean {
  const start = bookingStart(booking);
  const end = bookingEnd(booking);
  return start.isValid() && end.isValid() && end.isAfter(start);
}

export function statusLabel(status: Booking['status']): string {
  switch (status) {
    case 'pending':
      return 'In attesa';
    case 'approved':
      return 'Approvata';
    case 'rejected':
      return 'Rifiutata';
    default:
      return status;
  }
}
